/**
 * Review Modal Component
 * Allows users to write reviews for products from delivered orders
 */

import React, { useState, useEffect } from 'react';
import { X, Star, Send, Loader2 } from 'lucide-react';
import reviewService from '../../services/reviewService';

const ReviewModal = ({ orderId, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reviewableProducts, setReviewableProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submittedRating, setSubmittedRating] = useState(0);
  const [submittedProductName, setSubmittedProductName] = useState('');

  useEffect(() => {
    fetchReviewableProducts();
  }, [orderId]);

  const fetchReviewableProducts = async () => {
    setLoading(true);
    const result = await reviewService.getReviewableProducts(orderId);
    if (result.success) {
      setReviewableProducts(result.data.products);
      if (result.data.products.length > 0) {
        setSelectedProduct(result.data.products[0]);
      }
    } else {
      setError(result.error || 'Failed to load reviewable products');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedProduct) {
      setError('Please select a product to review');
      return;
    }
    
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setSubmittedRating(rating);
    setSubmittedProductName(selectedProduct.productName);
    setSubmitting(true);
    setError('');

    const result = await reviewService.createReview({
      productId: selectedProduct.productId,
      orderId,
      rating,
      title: title.trim() || undefined,
      comment: comment.trim() || undefined,
    });

    if (result.success) {
      setSuccessMessage(`Thank you for reviewing ${selectedProduct.productName}!`);
      // Remove the reviewed product from the list
      const remaining = reviewableProducts.filter(p => p.productId !== selectedProduct.productId);
      setReviewableProducts(remaining);
      
      // Reset form for next review or close
      setRating(0);
      setTitle('');
      setComment('');
      setSelectedProduct(remaining[0] || null);
      
      if (remaining.length === 0) {
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 1500);
      } else {
        setTimeout(() => setSuccessMessage(''), 2000);
      }
    } else {
      setError(result.error || 'Failed to submit review');
    }
    
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-[#548B2E]" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-800">Write a Review</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {reviewableProducts.length === 0 ? (
            <div className="text-center py-8">
              <div className="flex justify-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map(star => (
                  <Star
                    key={star}
                    className={`w-8 h-8 ${
                      star <= submittedRating
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-200 fill-gray-200'
                    }`}
                  />
                ))}
              </div>
              <p className="text-gray-800 font-semibold mb-1">
                {submittedProductName} reviewed!
              </p>
              <p className="text-sm text-gray-500">Thank you for your feedback.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Success Message */}
              {successMessage && (
                <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                  {successMessage}
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Product Selection */}
              {reviewableProducts.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Product
                  </label>
                  <select
                    value={selectedProduct?.productId || ''}
                    onChange={(e) => {
                      const product = reviewableProducts.find(
                        p => p.productId === Number(e.target.value)
                      );
                      setSelectedProduct(product);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                  >
                    {reviewableProducts.map(product => (
                      <option key={product.productId} value={product.productId}>
                        {product.productName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Current Product */}
              {selectedProduct && reviewableProducts.length === 1 && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Reviewing:</p>
                  <p className="font-medium text-gray-800">{selectedProduct.productName}</p>
                </div>
              )}

              {/* Star Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating *
                </label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-1 focus:outline-none"
                    >
                      <Star
                        className={`w-8 h-8 transition-colors ${
                          star <= (hoverRating || rating)
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {rating === 1 && 'Poor'}
                  {rating === 2 && 'Fair'}
                  {rating === 3 && 'Good'}
                  {rating === 4 && 'Very Good'}
                  {rating === 5 && 'Excellent'}
                </p>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Review Title (optional)
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Summarize your experience"
                  maxLength={200}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                />
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Review (optional)
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Share your experience with this product..."
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent resize-none"
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || rating === 0}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Submit Review
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;